import { apiJson } from '@/lib/http';

// Use shared helper
const apiCall = <T = unknown>(endpoint: string, options: RequestInit = {}) => apiJson<T>(endpoint, options);

// Backend API response types
interface ApiResponse<T> {
  success: boolean;
  message: string;
  data: T;
}

// Usage statistics types
export interface UsageMetric {
  name: string;
  used: number;
  limit: number;
  unit: string;
  term: string;
  percentage: number;
  trend: string;
}

export interface LastActivity {
  eventType: string;
  projectId: string;
  userId: string;
  timestamp: string;
  timeAgo: string;
}

export interface UsageHistoryItem {
  date: string;
  eventType: string;
  userId: string;
  count: number;
}

export interface ChatbotUsageItem {
  date: string;
  projectId?: string | null;
  credits: number;
}

export interface UsageStatsResponse {
  usageMetrics: UsageMetric[];
  lastActivity: LastActivity;
  usageHistory: UsageHistoryItem[];
  chatbotUsage: ChatbotUsageItem[];
  subscriptionName?: string;
}

export interface VoiceUsage {
  sttSeconds: number;
  ttsSeconds: number;
  totalSeconds: number;
  totalMinutesCeil: number;
}

export interface UsageStatsRequest {
  timeRange?: string;
  startDate?: string;
  endDate?: string;
  projectId?: string;
}

export interface ApiCallStatistic {
  totalCalls: number;
  periodStart: string;
  periodEnd: string;
}

export interface MemoryStatistic {
  totalMemories: number;
  uniqueUsers: number;
  memoryObjects: number;
}

export const statisticService = {
  // Get comprehensive usage statistics
  async getUsageStats(request: UsageStatsRequest = {}): Promise<UsageStatsResponse> {
    try {
      const queryParams = new URLSearchParams();
      if (request.timeRange) queryParams.append('timeRange', request.timeRange);
      if (request.startDate) queryParams.append('startDate', request.startDate);
      if (request.endDate) queryParams.append('endDate', request.endDate);
      if (request.projectId) queryParams.append('projectId', request.projectId);
      
      const endpoint = `/Statistic${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;
      const response: ApiResponse<UsageStatsResponse> = await apiCall(endpoint);
      const data = response.data;
      return {
        ...data,
        usageMetrics: data.usageMetrics ?? [],
        usageHistory: data.usageHistory ?? [],
        chatbotUsage: data.chatbotUsage ?? [],
      } as UsageStatsResponse;
    } catch (error) {
      console.error('Error fetching usage statistics:', error);
      throw error;
    }
  },

  // Get API call statistics
  async getApiCallStatistics(startDate?: string, endDate?: string, projectId?: string): Promise<ApiCallStatistic> {
    try {
      const queryParams = new URLSearchParams();
      if (startDate) queryParams.append('startDate', startDate);
      if (endDate) queryParams.append('endDate', endDate);
      if (projectId) queryParams.append('projectId', projectId);
      
      const endpoint = `/Statistic/api-calls${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;
      const response: ApiResponse<ApiCallStatistic> = await apiCall(endpoint);
      return response.data;
    } catch (error) {
      console.error('Error fetching API call statistics:', error);
      throw error;
    }
  },

  // Get memory statistics
  async getMemoryStatistics(projectId?: string): Promise<MemoryStatistic> {
    try {
      const endpoint = `/Statistic/memory${projectId ? `?projectId=${encodeURIComponent(projectId)}` : ''}`;
      const response: ApiResponse<MemoryStatistic> = await apiCall(endpoint);
      return response.data;
    } catch (error) {
      console.error('Error fetching memory statistics:', error);
      throw error;
    }
  },

  // Get last activity
  async getLastActivity(projectId?: string): Promise<LastActivity> {
    try {
      const endpoint = `/Statistic/last-activity${projectId ? `?projectId=${encodeURIComponent(projectId)}` : ''}`;
      const response: ApiResponse<LastActivity> = await apiCall(endpoint);
      return response.data;
    } catch (error) {
      console.error('Error fetching last activity:', error);
      throw error;
    }
  },

  // Get usage history
  async getUsageHistory(startDate?: string, endDate?: string, projectId?: string): Promise<UsageHistoryItem[]> {
    try {
      const queryParams = new URLSearchParams();
      if (startDate) queryParams.append('startDate', startDate);
      if (endDate) queryParams.append('endDate', endDate);
      if (projectId) queryParams.append('projectId', projectId);
      
      const endpoint = `/Statistic/usage-history${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;
      const response: ApiResponse<UsageHistoryItem[]> = await apiCall(endpoint);
      return response.data;
    } catch (error) {
      console.error('Error fetching usage history:', error);
      throw error;
    }
  },

  async getVoiceUsage(projectId?: string): Promise<VoiceUsage> {
    const endpoint = `/Statistic/voice-usage${projectId ? `?projectId=${encodeURIComponent(projectId)}` : ''}`;
    const response: ApiResponse<VoiceUsage> = await apiCall(endpoint);
    return response.data;
  },
}; 